import React, { ReactNode } from 'react';

import { GlossaryTerm, GlossaryTermMap } from 'src/types/glossary';
import { GlossaryTermHighlight } from 'src/components/GlossaryTerm/GlossaryTermHighlight';

import { requiresExactCaseMatch } from './glossary-stopwords';

/**
 * HTML element names where glossary matching must be skipped entirely —
 * including the whole subtree rooted at any of these elements. See
 * Requirement 2.7.
 */
const PRESERVED_HTML_ELEMENTS: ReadonlySet<string> = new Set([
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'code',
    'pre',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
]);

/**
 * CSS classes that mark an element as a preserved subtree, independent
 * of its tag name. Xyleme emits inline / block code using a plain
 * `<span class="InLineCode">` or `<div class="ProgramCode">` rather
 * than a `<code>` or `<pre>` tag, and useRenderContent only wraps those
 * in a real `<code>` React element on the OUTPUT side — the parse-time
 * ancestor walk still sees the original span/div, so the tag-name
 * check alone would let glossary highlighting into code snippets and
 * defeat Requirement 2.7.
 *
 * Match is case-insensitive and tolerates these as one token among
 * others on `class` (e.g. `class="InLineCode highlighted"`).
 */
const PRESERVED_HTML_CLASSES: ReadonlySet<string> = new Set([
    'inlinecode',
    'programcode',
]);

export function isPreservedElement(
    nodeOrTag:
        | {
              name?: string;
              attribs?: Record<string, string>;
          }
        | null
        | undefined
): boolean {
    if (!nodeOrTag) {
        return false;
    }
    const name = (nodeOrTag.name ?? '').toLowerCase();
    if (PRESERVED_HTML_ELEMENTS.has(name)) {
        return true;
    }
    const attribs = nodeOrTag.attribs ?? {};
    // Preserved by CSS class (code snippets emitted as span/div).
    const classAttr = attribs.class;
    if (typeof classAttr === 'string' && classAttr.length > 0) {
        for (const token of classAttr.split(/\s+/)) {
            if (PRESERVED_HTML_CLASSES.has(token.toLowerCase())) {
                return true;
            }
        }
    }
    // Any `on*` attribute marks the element as interactive — Requirement
    // 2.7 says to preserve "any element with an inline event-handler
    // attribute", not just the common click/key/focus ones. Match the
    // whole family (onmouseover, oninput, oncontextmenu, onwheel, …)
    // instead of an incomplete allowlist that silently drops some.
    for (const attr of Object.keys(attribs)) {
        if (attr.toLowerCase().startsWith('on')) {
            return true;
        }
    }
    return false;
}

/**
 * Character classes for whole-word boundaries, matching Requirement 2.2.
 *
 * A term matches only when the preceding and following characters are
 * either absent (start/end of the text node) or non-alphanumeric. "car"
 * does NOT match inside "cardiac" because 'd' is alphanumeric.
 *
 * We intentionally use ASCII alphanumeric here. Glossary terms in the AR
 * wiki are English acronyms and short phrases; adding full Unicode letter
 * classes would widen the match surface without a product reason.
 */
function isWordChar(ch: string | undefined): boolean {
    if (ch === undefined || ch.length === 0) {
        return false;
    }
    const code = ch.charCodeAt(0);
    return (
        (code >= 48 && code <= 57) || // 0-9
        (code >= 65 && code <= 90) || // A-Z
        (code >= 97 && code <= 122) // a-z
    );
}

/**
 * Escape a term for use inside a RegExp. We don't actually use a regex at
 * runtime for matching (we scan character by character for performance and
 * to control boundary semantics exactly), but keeping this helper around
 * documents the escape rule in one place.
 */
export function escapeForRegex(input: string): string {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface Match {
    /** Start offset within the text, inclusive. */
    start: number;
    /** End offset within the text, exclusive. */
    end: number;
    entry: GlossaryTerm;
}

/**
 * Build a lowercase projection of `text` that stays 1:1 with its indices.
 *
 * We cannot use `text.toLowerCase()` directly: a handful of Unicode code
 * points expand on lowercase (notably the Turkish capital İ (U+0130),
 * which lowercases to `i` + combining dot above (U+0307)). That shifts
 * every subsequent index in the lowered string, and the boundary and
 * `text.slice(match.start, match.end)` reads downstream — which still
 * index into the ORIGINAL text — would then slice the wrong characters.
 *
 * Lowercasing per source code unit keeps lengths aligned. In the rare
 * case a single code unit lowercases to multiple code units, we keep the
 * original character so alignment holds. That may miss a match on an
 * expanding character, which is acceptable — glossary terms are English
 * ASCII acronyms per Requirement 1.11, so the trade-off never bites us
 * in practice but we no longer corrupt highlights on topic content that
 * happens to contain such a character elsewhere.
 */
function toLowerPreservingLength(text: string): string {
    let out = '';
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const low = ch.toLowerCase();
        out += low.length === ch.length ? low : ch;
    }
    return out;
}

/**
 * Finds all glossary term occurrences in `text`, honoring:
 *   - case-insensitive matching by default (so lowercase uses of a
 *     canonical uppercase acronym — "the arc team" vs the `ARC` entry
 *     — still highlight).
 *   - case-SENSITIVE matching for a narrow class of acronyms: short,
 *     pure-alpha, all-uppercase terms whose lowercase form is a common
 *     English function word (`FROM`, `UP`, `HAS`, `WAY`, …). Without
 *     this exception every "from" in prose would highlight as the
 *     `FROM` glossary entry. See `glossary-stopwords.ts` for the list
 *     and the add/remove policy.
 *   - whole-word boundaries (ASCII alphanumeric)
 *   - longest-match preference when two terms would overlap at the same
 *     start position
 *   - no nested highlights — overlapping matches are resolved by taking
 *     the longest non-overlapping set greedily (left-to-right).
 */
export function findMatches(text: string, terms: GlossaryTermMap): Match[] {
    if (text.length === 0 || terms.size === 0) {
        return [];
    }

    // Index-aligned lowercase projection: lowered[i] always corresponds to
    // text[i]. See toLowerPreservingLength above.
    const lowered = toLowerPreservingLength(text);
    const candidates: Match[] = [];

    // For every start position in the text, collect candidate matches.
    // This is O(text.length * terms.size) in the worst case, but term keys
    // are short (<= 256 chars per Requirement 1.11, typically < 20) and
    // most keys fail on the first character comparison inside startsWith,
    // so in practice this is fast enough for topic content up to 50 000
    // characters (Requirement 2.9).
    for (const [key, entry] of terms) {
        if (key.length === 0) {
            continue;
        }
        const needsExactCase = requiresExactCaseMatch(entry.term);
        let from = 0;
        while (from <= lowered.length - key.length) {
            const idx = lowered.indexOf(key, from);
            if (idx === -1) {
                break;
            }
            const end = idx + key.length;
            // Case-sensitive gate: for a stopword-collision acronym, the
            // substring in the original text must equal the canonical
            // surface form exactly. "from the kitchen" fails this against
            // the `FROM` entry; "the FROM pipeline" passes.
            if (needsExactCase && text.slice(idx, end) !== entry.term) {
                from = idx + 1;
                continue;
            }
            const before = idx === 0 ? undefined : text[idx - 1];
            const after = end >= text.length ? undefined : text[end];
            if (!isWordChar(before) && !isWordChar(after)) {
                candidates.push({ start: idx, end, entry });
            }
            from = idx + 1;
        }
    }

    if (candidates.length === 0) {
        return [];
    }

    // Longest-match resolution. Sort by start ascending, length descending.
    // Then take candidates greedily in order, skipping anything that
    // overlaps the last accepted match. This guarantees:
    //   - at any given start position the longest term wins (because the
    //     sort puts the longest-at-this-start first).
    //   - no overlapping matches (because we skip anything whose start is
    //     before the last accepted end).
    candidates.sort((a, b) => {
        if (a.start !== b.start) {
            return a.start - b.start;
        }
        return b.end - a.end;
    });

    const accepted: Match[] = [];
    let lastAcceptedEnd = -1;
    for (const match of candidates) {
        if (match.start < lastAcceptedEnd) {
            continue;
        }
        accepted.push(match);
        lastAcceptedEnd = match.end;
    }
    return accepted;
}

/**
 * Transform a plain-text string into a React children array where each
 * glossary term occurrence is wrapped in a `<GlossaryTermHighlight>`.
 *
 * Non-matching text is returned as-is in its original casing. Returns the
 * original string unchanged (wrapped as a single-element array) when there
 * are no matches or no terms configured.
 */
export function highlightGlossaryTerms(
    text: string,
    terms: GlossaryTermMap
): ReactNode[] {
    const matches = findMatches(text, terms);
    if (matches.length === 0) {
        return [text];
    }

    const parts: ReactNode[] = [];
    let cursor = 0;
    matches.forEach((match, index) => {
        if (match.start > cursor) {
            parts.push(text.slice(cursor, match.start));
        }
        const surface = text.slice(match.start, match.end);
        parts.push(
            <GlossaryTermHighlight
                key={`glossary-${match.start}-${index}`}
                displayText={surface}
                entry={match.entry}
            />
        );
        cursor = match.end;
    });
    if (cursor < text.length) {
        parts.push(text.slice(cursor));
    }
    return parts;
}
